import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, ChevronLeft, Truck, RotateCcw, Shield, Check } from "lucide-react";
import axios from "axios";
import { useCart } from "../context/CartContext";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StarRating({ rating, size = 14 }) {
  return (
    <div className="flex gap-0.5" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={i <= rating ? "fill-[#0A0A0A] text-[#0A0A0A]" : "text-[#D4D4D4]"}
          size={size}
        />
      ))}
    </div>
  );
}

function ReviewCard({ review }) {
  return (
    <div data-testid="review-card" className="border-t border-[#E5E5E5] pt-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <StarRating rating={review.rating} />
          {review.verified && (
            <span className="flex items-center gap-1 text-[10px] tracking-[0.1em] uppercase text-[#8B6F47]" style={{ fontStyle: "normal" }}>
              <Check className="w-3 h-3" /> Verified
            </span>
          )}
        </div>
        <span className="text-xs text-[#888]" style={{ fontStyle: "normal" }}>{review.date}</span>
      </div>
      <p className="text-sm text-[#0A0A0A] tracking-wide mb-1">{review.title}</p>
      <p className="text-sm text-[#555] leading-relaxed tracking-wide">{review.body}</p>
      <p className="text-xs text-[#888] mt-2" style={{ fontStyle: "normal" }}>{review.author}</p>
    </div>
  );
}

export default function ProductDetailPage() {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [selectedSize, setSelectedSize] = useState("M");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchData = async () => {
      try {
        const [prodRes, revRes] = await Promise.all([
          axios.get(`${API}/products/${id}`),
          axios.get(`${API}/products/${id}/reviews`),
        ]);
        setProduct(prodRes.data);
        setReviews(revRes.data);
      } catch (err) {
        console.error("Failed to fetch product:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const avgRating = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  const handlePreOrder = () => {
    if (product) {
      addItem(product, selectedSize);
      toast.success("Added to pre-order", {
        description: `${product.name} — Size ${selectedSize}`,
      });
    }
  };

  if (loading) {
    return (
      <main className="pt-28 min-h-screen bg-[#FDFDFD]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <div className="animate-pulse bg-[#F3F3F3] aspect-[3/4]" />
            <div className="animate-pulse space-y-4 pt-8">
              <div className="h-4 bg-[#F3F3F3] w-1/3" />
              <div className="h-8 bg-[#F3F3F3] w-2/3" />
              <div className="h-4 bg-[#F3F3F3] w-1/4" />
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="pt-28 min-h-screen bg-[#FDFDFD] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-[#0A0A0A] mb-4">Product not found</h1>
          <Link to="/shop" className="text-sm text-[#0A0A0A] border-b border-[#0A0A0A] pb-0.5">Back to Shop</Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="product-detail-page" className="bg-[#FDFDFD]">
      <SEO
        title={product.name}
        path={`/product/${product.id}`}
        description={`${product.name} — CHF ${product.price.toFixed(2)}. ${product.description} Pre-order now from Padlr.`}
        image={product.image}
        type="product"
        product={{
          name: product.name,
          description: product.description,
          image: product.image,
          price: product.price,
          rating: avgRating > 0 ? avgRating : undefined,
          reviewCount: reviews.length > 0 ? reviews.length : undefined,
        }}
      />

      {/* Breadcrumb */}
      <div className="pt-28 sm:pt-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/shop" data-testid="back-to-shop"
            className="inline-flex items-center gap-1 text-xs tracking-[0.1em] uppercase text-[#888] hover:text-[#0A0A0A] transition-colors"
            style={{ fontStyle: "normal" }}>
            <ChevronLeft className="w-3.5 h-3.5" /> Back to Shop
          </Link>
        </div>
      </div>

      {/* Product */}
      <section className="py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
            {/* Image */}
            <div className="relative overflow-hidden bg-[#F3F3F3]">
              <img src={product.image} alt={product.name} data-testid="product-detail-image"
                className="w-full aspect-[3/4] object-cover" />
              <span className="absolute top-4 left-4 px-4 py-1.5 text-[11px] tracking-[0.15em] uppercase bg-[#0A0A0A] text-white"
                style={{ fontStyle: "normal" }}>Coming Soon</span>
            </div>

            {/* Details */}
            <div className="lg:py-4">
              <p className="text-xs tracking-[0.2em] uppercase text-[#888] mb-3" style={{ fontStyle: "normal" }}>
                {product.category}
              </p>
              <h1 data-testid="product-detail-name"
                className="text-2xl sm:text-3xl lg:text-4xl tracking-tighter text-[#0A0A0A] leading-tight mb-4">
                {product.name}
              </h1>

              {reviews.length > 0 && (
                <div className="flex items-center gap-3 mb-6">
                  <StarRating rating={Math.round(avgRating)} />
                  <span className="text-sm text-[#888]" style={{ fontStyle: "normal" }}>
                    {avgRating} ({reviews.length} {reviews.length === 1 ? "review" : "reviews"})
                  </span>
                </div>
              )}

              <div className="flex items-center gap-3 mb-8">
                <span data-testid="product-detail-price" className="text-xl text-[#0A0A0A]" style={{ fontStyle: "normal" }}>
                  CHF {product.price.toFixed(2)}
                </span>
                {product.original_price && (
                  <span className="text-lg text-[#888] line-through" style={{ fontStyle: "normal" }}>
                    CHF {product.original_price.toFixed(2)}
                  </span>
                )}
              </div>

              <p className="text-sm text-[#555] leading-relaxed tracking-wide mb-8">{product.description}</p>

              {/* Size selector */}
              <div className="mb-8">
                <p className="text-xs tracking-[0.2em] uppercase text-[#0A0A0A] mb-3" style={{ fontStyle: "normal" }}>Size</p>
                <div className="flex gap-2" data-testid="size-selector">
                  {product.sizes.map((size) => (
                    <button key={size} data-testid={`size-${size}`}
                      onClick={() => setSelectedSize(size)}
                      className={`w-12 h-12 text-xs tracking-wider transition-all duration-200 ${
                        selectedSize === size
                          ? "bg-[#0A0A0A] text-white"
                          : "border border-[#E5E5E5] text-[#0A0A0A] hover:border-[#0A0A0A]"
                      }`}
                      style={{ fontStyle: "normal" }}>{size}</button>
                  ))}
                </div>
              </div>

              {/* Pre-Order button */}
              <button data-testid="preorder-add-button" onClick={handlePreOrder}
                className="w-full bg-[#8B6F47] text-white py-4 text-sm tracking-[0.2em] uppercase hover:bg-[#7A6140] transition-colors active:scale-[0.98] mb-3"
                style={{ fontStyle: "normal" }}>
                Pre-Order Now — CHF {product.price.toFixed(2)}
              </button>
              <p className="text-center text-xs text-[#888] tracking-wide mb-8" style={{ fontStyle: "normal" }}>
                No payment required now. We'll contact you before shipping.
              </p>

              {/* Trust badges */}
              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-[#E5E5E5]">
                <div className="text-center">
                  <Truck className="w-5 h-5 mx-auto mb-2 text-[#555]" strokeWidth={1.5} />
                  <p className="text-[10px] tracking-[0.1em] uppercase text-[#888]" style={{ fontStyle: "normal" }}>Free Shipping</p>
                  <p className="text-[10px] text-[#aaa]" style={{ fontStyle: "normal" }}>Over CHF 75</p>
                </div>
                <div className="text-center">
                  <RotateCcw className="w-5 h-5 mx-auto mb-2 text-[#555]" strokeWidth={1.5} />
                  <p className="text-[10px] tracking-[0.1em] uppercase text-[#888]" style={{ fontStyle: "normal" }}>Easy Returns</p>
                  <p className="text-[10px] text-[#aaa]" style={{ fontStyle: "normal" }}>30 Day Policy</p>
                </div>
                <div className="text-center">
                  <Shield className="w-5 h-5 mx-auto mb-2 text-[#555]" strokeWidth={1.5} />
                  <p className="text-[10px] tracking-[0.1em] uppercase text-[#888]" style={{ fontStyle: "normal" }}>Secure</p>
                  <p className="text-[10px] text-[#aaa]" style={{ fontStyle: "normal" }}>SSL Encrypted</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      {reviews.length > 0 && (
        <section data-testid="reviews-section" className="py-16 sm:py-20 border-t border-[#E5E5E5]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl sm:text-3xl tracking-tighter text-[#0A0A0A]">Customer Reviews</h2>
                <div className="flex items-center gap-2 mt-2">
                  <StarRating rating={Math.round(avgRating)} />
                  <span className="text-sm text-[#888]" style={{ fontStyle: "normal" }}>Based on {reviews.length} reviews</span>
                </div>
              </div>
              <div className="text-right">
                <span className="text-3xl text-[#0A0A0A]" style={{ fontStyle: "normal" }}>{avgRating}</span>
                <p className="text-xs text-[#888]" style={{ fontStyle: "normal" }}>out of 5</p>
              </div>
            </div>
            <div className="space-y-6">
              {reviews.map((review) => (<ReviewCard key={review.id} review={review} />))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </main>
  );
}
